/**
 * Helpers to read rent fields after TariffPlan v2 migration.
 * Legacy UI expected `currentTariff` / `groupTariffs`; API now exposes
 * `tariffName` (current period via getter) and `currency` enum.
 *
 * Place fields (variant B): preferred `startPlace` / `completionPlace`
 * `{ stationNumber, address, additionalAddress }`; legacy flat
 * `startStation` / `completionStation` kept as fallback.
 */

import { getCurrencySymbolForLocation, getCurrencySymbol } from '../utils/currency';

function trimStr(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

/** @returns {{ stationNumber?: string, address?: string, additionalAddress?: string } | null} */
export function getRentStartPlace(rent) {
  if (!rent || typeof rent !== 'object') return null;
  if (rent.startPlace && typeof rent.startPlace === 'object') return rent.startPlace;
  return null;
}

/** @returns {{ stationNumber?: string, address?: string, additionalAddress?: string } | null} */
export function getRentCompletionPlace(rent) {
  if (!rent || typeof rent !== 'object') return null;
  if (rent.completionPlace && typeof rent.completionPlace === 'object') return rent.completionPlace;
  return null;
}

/** Placement point name (`Station.additionalAddress`), with optional legacy joined-string parse.
 * @param legacyJoined when true, treat `legacyJoinedAddress` as `address,additionalAddress`
 *        (completionStation). startStation is address-only — never pass legacyJoined for start.
 */
export function getRentPlacePointName(place, legacyJoinedAddress, { legacyJoined = false } = {}) {
  const fromPlace = trimStr(place?.additionalAddress);
  if (fromPlace) return fromPlace;
  if (!legacyJoined) return null;
  // Legacy completionStation often "address,additionalAddress" (address may contain commas)
  const legacy = trimStr(legacyJoinedAddress);
  if (legacy && legacy.includes(',')) {
    const after = legacy.slice(legacy.lastIndexOf(',') + 1).trim();
    if (after) return after;
  }
  return null;
}

export function getRentPlaceAddress(place, legacyJoinedAddress, { legacyJoined = false } = {}) {
  const fromPlace = trimStr(place?.address);
  if (fromPlace) return fromPlace;
  const legacy = trimStr(legacyJoinedAddress);
  if (!legacy) return null;
  if (legacyJoined && legacy.includes(',')) {
    const before = legacy.slice(0, legacy.lastIndexOf(',')).trim();
    return before || null;
  }
  return legacy;
}

export function getRentPlaceStationNumber(place, legacyStationNumber) {
  return trimStr(place?.stationNumber) || trimStr(legacyStationNumber) || null;
}

/**
 * Return-station number for admin UI / cross-link.
 * Mirrors backend Rent.getCompletionStationNumber(): prefer place/API field,
 * then legacy same-station fallback when return address matches start.
 */
export function resolveCompletionStationNumber(rent) {
  if (!rent || typeof rent !== 'object') return null;

  const completionPlace = getRentCompletionPlace(rent);
  const direct = getRentPlaceStationNumber(completionPlace, rent.completionStationNumber);
  if (direct) return direct;

  const startPlace = getRentStartPlace(rent);
  const startNumber = getRentPlaceStationNumber(startPlace, rent.stationNumber);
  if (!startNumber) return null;

  const hasCompletionText =
    !!getRentPlaceAddress(completionPlace, rent.completionStation, { legacyJoined: true }) ||
    !!getRentPlacePointName(completionPlace, rent.completionStation, { legacyJoined: true }) ||
    !!trimStr(rent.completionStation) ||
    rent.completionStationSlot != null;

  if (!hasCompletionText) return null;

  const startAddr =
    getRentPlaceAddress(startPlace, rent.startStation) || trimStr(rent.startStation);
  const completionAddr =
    getRentPlaceAddress(completionPlace, rent.completionStation, { legacyJoined: true }) ||
    trimStr(rent.completionStation);

  if (startAddr && completionAddr) {
    const normStart = startAddr.trim();
    const normComp = completionAddr.trim();
    if (
      normComp === normStart ||
      normComp.startsWith(`${normStart},`) ||
      normComp.startsWith(normStart)
    ) {
      return startNumber;
    }
  }

  return null;
}

/**
 * One-line label for tables: "Point · Address" (or either alone).
 * Falls back to legacy flat address string when structured place is absent.
 * @param options.legacyJoined — true for completionStation; false/omit for startStation
 */
export function formatRentPlaceLabel(place, legacyJoinedAddress, options = {}) {
  const point = getRentPlacePointName(place, legacyJoinedAddress, options);
  const address = getRentPlaceAddress(place, legacyJoinedAddress, options);
  if (point && address && point !== address) return `${point} · ${address}`;
  return point || address || null;
}

/** Plan / period / id pieces used for display and Tariffs cross-link. */
export function getRentTariffParts(rent) {
  if (!rent || typeof rent !== 'object') {
    return { planName: null, periodName: null, planId: null };
  }

  const planName = trimStr(
    rent.tariffPlan?.name ||
      rent.groupTariffs?.name ||
      rent.groupTariffs?.groupName ||
      rent.tariffPlanName,
  );

  const periodName = trimStr(
    rent.currentTariff?.tariffName ||
      (typeof rent.tariffName === 'string' ? rent.tariffName : null),
  );

  const planId = trimStr(
    rent.tariffPlan?.id ||
      rent.tariffPlanId ||
      rent.groupTariffs?.id,
  );

  return { planName, periodName, planId };
}

/**
 * Human-readable tariff for rent detail / lists.
 * Prefers plan name when present; falls back to current period name.
 */
export function getRentTariffLabel(rent) {
  const { planName, periodName } = getRentTariffParts(rent);
  if (planName && periodName && planName !== periodName) return `${planName} · ${periodName}`;
  return planName || periodName || null;
}

/**
 * Best query string for Tariffs page cross-link (plan name > id > period).
 * Stored in sessionStorage as `crossLinkTariffName`.
 */
export function getRentTariffCrossLinkQuery(rent) {
  const { planName, periodName, planId } = getRentTariffParts(rent);
  return planName || planId || periodName || null;
}

/** Currency symbol for a rent row (currency enum or legacy location). */
export function getRentCurrencySymbol(rent) {
  if (!rent || typeof rent !== 'object') return getCurrencySymbolForLocation();
  if (rent.currency) return getCurrencySymbol(rent.currency);
  const location =
    rent.groupTariffs?.location ||
    rent.tariffPlan?.location ||
    null;
  return getCurrencySymbolForLocation(location);
}

/**
 * Find a tariff group in a loaded list by cross-link query
 * (exact/partial name, id, or matching period/part name).
 */
export function findTariffByCrossLinkQuery(list, query) {
  const q = trimStr(query);
  if (!q || !Array.isArray(list) || list.length === 0) return null;
  const qLower = q.toLowerCase();

  const byExactName = list.find((tg) => trimStr(tg?.name)?.toLowerCase() === qLower);
  if (byExactName) return byExactName;

  const byId = list.find((tg) => trimStr(tg?.id)?.toLowerCase() === qLower);
  if (byId) return byId;

  const byPartExact = list.find((tg) =>
    Array.isArray(tg?.parts) &&
    tg.parts.some((p) => trimStr(p?.name)?.toLowerCase() === qLower || trimStr(p?.tariffName)?.toLowerCase() === qLower),
  );
  if (byPartExact) return byPartExact;

  const byPartialName = list.find((tg) => trimStr(tg?.name)?.toLowerCase().includes(qLower));
  if (byPartialName) return byPartialName;

  return null;
}
